import debounce from 'debounce-promise';
import { ReactNode, useCallback } from 'react';
import { Control, Controller, ControllerRenderProps, FieldError, FieldPath, FieldValues, Path } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { components, GroupBase, SingleValue } from 'react-select';
import Select from 'react-select/async';
import { InputSpecificProps } from 'react-select/dist/declarations/src/components/Input';

import Error from '@components/fields/Error';
import RequiredFieldIndicator from '@components/fields/RequiredFieldIndicator';
import styles from '@components/fields/Select.module.css';
import Nbsp from '@components/Nbsp';
import InputLabel from '@components/typography/InputLabel';
import InputSubLabel from '@components/typography/InputSubLabel';
import { selectStyles } from '@utils/formUtils';
import { ImageName, imageUrl } from '@utils/imageUtils';

export type Option = {
  disabled?: boolean;
  imageName?: ImageName;
  label: string;
  value: string;
};

type Props<FormData extends FieldValues> = {
  autofocus?: boolean;
  control: Control<FormData>;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  'data-test'?: string;
  defaultOptions?: Option[] | boolean;
  disabled?: boolean;
  error: FieldError | undefined;
  label?: string | ReactNode;
  name: FieldPath<FormData>;
  onSearch?: (inputValue: string) => Promise<Option[]>;
  onSelection?: (option: Option) => void;
  required?: boolean;
  subLabel?: string | ReactNode;
  value: Option | null;
};

const Input = ({ ...props }) => (
  <components.Input {...(props as InputSpecificProps<unknown, boolean, GroupBase<unknown>>)} data-lpignore="true" />
);

const AsyncSelect = <FormData extends FieldValues>({
  autofocus = false,
  control,
  'data-test': dataCy,
  defaultOptions = [],
  disabled = false,
  error,
  label,
  name,
  onSearch,
  onSelection,
  required,
  subLabel,
  value
}: Props<FormData>) => {
  const intl = useIntl();

  const formatOptionLabel = ({ imageName, label }: Option) => (
    <div className={styles.label}>
      <div>{label}</div>
      <div>
        {imageName && (
          <img
            alt={intl.formatMessage({ id: 'accessibility.altTags.searchSelectItem' })}
            className={imageName === 'poweredByGoogle' ? undefined : styles.searchSelectImg}
            src={imageUrl(imageName)}
          />
        )}
      </div>
    </div>
  );

  const generateHandleChange = useCallback(
    (onChange: ControllerRenderProps<FormData, Path<FormData>>['onChange']) => (option: SingleValue<Option>) => {
      if (option) {
        onChange(option.value);
        onSelection?.(option);
      }
    },
    [onSelection]
  );

  return (
    <>
      <div className={`${styles.wrapperItem}`}>
        {label && (
          <div>
            <InputLabel>{label}</InputLabel>
            {subLabel && (
              <>
                <Nbsp />
                <InputSubLabel>
                  <FormattedMessage id="dictionary.punctuation.openParen" />
                  {subLabel}
                  <FormattedMessage id="dictionary.punctuation.closeParen" />
                </InputSubLabel>
              </>
            )}
            {required && <InputLabel>{required && <RequiredFieldIndicator />}</InputLabel>}
          </div>
        )}
        <Controller
          control={control}
          name={name}
          render={({ field: { name, onChange } }) => (
            <div className={styles.margin}>
              <Select
                aria-describedby={error ? `${name}-error` : ''}
                aria-invalid={error?.message?.length ? true : false}
                aria-label={name}
                aria-required={required}
                autoFocus={autofocus}
                classNamePrefix="react-select-async"
                components={{ Input }}
                data-test={dataCy}
                defaultOptions={defaultOptions}
                formatOptionLabel={formatOptionLabel}
                id={`${name}-input`}
                isDisabled={disabled}
                isOptionDisabled={option => !!option.disabled}
                loadOptions={onSearch && debounce(onSearch, 350)}
                menuPortalTarget={document.body}
                onChange={generateHandleChange(onChange)}
                styles={selectStyles}
                value={value}
              />
            </div>
          )}
        />
      </div>
      {error && (
        <div className={styles.errorWrapper} id={`${name}-error`}>
          <Error error={error} />
        </div>
      )}
    </>
  );
};

export default AsyncSelect;
